import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Formik, Form } from "formik";
import { saveAs } from "file-saver";
import {
  Alert,
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import LoadingButton from "@material-ui/lab/LoadingButton";
import CloseIcon from "@material-ui/icons/Close";
import VisibilityOutlinedIcon from "@material-ui/icons/VisibilityOutlined";

import { tareasApi } from "src/services/tareasApi";
import { dteApi } from "src/services/dteApi";
import { useObtenerDatosEmisorQuery } from "src/services/facturasApi";
import { obtenerValorMoneda } from "src/utils/sharedFunctions";
import { obtenerRutEmisor, DATOS_EMISOR_FALLBACK } from "src/utils/datosEmisor";
import { useSnackbar } from "src/components/snackbar";
import PreviewFacturaBorrador from "src/components/tareas/previewFactura/PreviewFacturaBorrador";
import { AlertaModalV2 } from "../../AlertaSwall";

import { facturarTareaSchema } from "./validationSchema";
import ReceptorSection from "./ReceptorSection";
import DetalleSection from "./DetalleSection";
import FacturacionSection from "./FacturacionSection";

export default function FacturarDialog({
  open,
  setOpen,
  tarea,
  monedas,
  recargarFacturas,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));

  const { sucursal: sucursalObtenida } = useSelector(
    (state) => state.auth.user,
  );
  const { enqueueSnackbar } = useSnackbar();

  const {
    data: tareaCompleta,
    isLoading: cargandoTarea,
    isError: errorTarea,
  } = tareasApi.useGetTareaQuery(tarea.id);

  const [crearDTE] = dteApi.useCrearDTEMutation();
  const [actualizarEstadoFacturadoTarea] =
    tareasApi.useUpdateTareaFacturadaMutation();

  const [errorEmision, setErrorEmision] = useState(null);
  const [openPreview, setOpenPreview] = useState(false);

  const rutEmisor = obtenerRutEmisor(sucursalObtenida);
  const { data: emisorSII } = useObtenerDatosEmisorQuery(rutEmisor);
  const datosEmisor = emisorSII ?? DATOS_EMISOR_FALLBACK;

  const clienteOriginal = tareaCompleta?.cliente;
  const { data: giroSIIMismaEmpresa } = dteApi.useObtenerInfoContribuyenteQuery(
    clienteOriginal?.rut,
    {
      skip: !clienteOriginal?.rut || !openPreview,
    },
  );

  const construirReceptorPreview = (values) => {
    if (values.tipoFacturacion === "OtraEmpresa") {
      if (!values.rutReceptor) return null;
      return {
        rut: values.rutReceptor,
        razonSocial: values.razonSocialReceptor,
        direccion: values.direccionReceptor,
        comuna: values.comunaReceptor,
        giro: values.giroReceptor,
      };
    }
    if (!clienteOriginal) return null;
    return {
      rut: clienteOriginal.rut,
      razonSocial: clienteOriginal.nombre,
      direccion: clienteOriginal.direccion,
      comuna: clienteOriginal.comuna,
      giro: giroSIIMismaEmpresa?.GiroRecep || null,
    };
  };

  const valoresIniciales = useMemo(() => {
    if (!tareaCompleta || !monedas) return null;

    const { descuento, valorTarea, tipoMonedaTarea } = tareaCompleta;

    const valorTipoMoneda =
      tipoMonedaTarea === "PESOS"
        ? 1
        : obtenerValorMoneda(monedas, tipoMonedaTarea);

    if (!valorTipoMoneda || valorTipoMoneda <= 0) {
      return { _error: "MONEDA_NO_DISPONIBLE", tipoMonedaTarea };
    }

    const descuentoPorcentaje = Number(descuento) || 0;
    const valorTareaOriginal = Number(valorTarea) || 0;
    const totalPagarOriginal =
      valorTareaOriginal - valorTareaOriginal * descuentoPorcentaje * 0.01;
    const totalPagarCLP = Math.round(totalPagarOriginal * valorTipoMoneda);

    const totalFacturadoCLP = (tarea.facturas ?? []).reduce(
      (total, factura) => {
        if (
          factura.estadoFactura !== "ANULADO" &&
          factura.estadoFactura !== "INCOBRABLE"
        ) {
          return total + Number(factura.total);
        }
        return total;
      },
      0,
    );

    const totalRestanteCLP = totalPagarCLP - totalFacturadoCLP;

    return {
      tipoFacturacion: "MismaEmpresa",
      rutReceptor: "",
      razonSocialReceptor: "",
      direccionReceptor: "",
      comunaReceptor: "",
      giroReceptor: "",

      glosa: tareaCompleta.nombre || "",
      descripcion: "",

      abono: "NO",
      porcentaje: "",
      montoFacturar: totalRestanteCLP > 0 ? totalRestanteCLP : 0,
      formatoPago: "2",

      _meta: {
        tipoMonedaTarea,
        valorTipoMoneda,
        valorTareaOriginal,
        descuentoPorcentaje,
        totalPagarOriginal,
        totalPagarCLP,
        totalFacturadoCLP,
        totalRestanteCLP,
      },
    };
  }, [tareaCompleta, monedas, tarea.facturas]);

  const solicitarCierre = async (dirty) => {
    if (!dirty) {
      setOpen(false);
      return;
    }
    const result = await AlertaModalV2({
      title: "¿Descartar los cambios?",
      text: "Se perderán los datos ingresados en el formulario.",
      confirmButtonText: "Sí, descartar",
      confirmButtonColor: "#d32f2f",
      icon: "warning",
    }).fire();
    if (result.isConfirmed) setOpen(false);
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    setErrorEmision(null);

    const clienteAFacturar =
      values.tipoFacturacion === "OtraEmpresa"
        ? values.rutReceptor
        : tareaCompleta.refCliente;

    const monto = Number(values.montoFacturar);
    const cerrarTarea = monto === Number(values._meta.totalRestanteCLP);

    const confirmacion = await AlertaModalV2({
      title: "¿Generar el documento tributario?",
      text: "Se emitirá la factura electrónica con los datos ingresados.",
      confirmButtonText: "Sí, generar",
      confirmButtonColor: "#1976d2",
      icon: "question",
    }).fire();

    if (!confirmacion.isConfirmed) {
      setSubmitting(false);
      return;
    }

    const body = {
      refClienteReceptor: clienteAFacturar,
      montoFactura: monto,
      porcentajeIVA: "19",
      tipoDTE: 34,
      detalle: [
        {
          nombreDetalle: values.glosa,
          cantidad: 1,
          precio: monto,
          monto,
          descripcion: values.descripcion || "",
        },
      ],
      tipoFactura: "TAREA",
      formatoPago: values.formatoPago,
      montoPagado: "NO",
      metodoPago: "",
      idTarea: tareaCompleta.id,
      sucursal: sucursalObtenida.refSucursal,
    };

    try {
      const response = await crearDTE(body).unwrap();

      if (response.error) {
        setErrorEmision(
          response.message || "Error al generar el documento tributario",
        );
        setSubmitting(false);
        return;
      }

      await actualizarEstadoFacturadoTarea({
        id: tarea.id,
        cerrarTarea,
      }).unwrap();

      saveAs(
        "data:application/pdf;base64," + response.PDF,
        `34_${tareaCompleta.refCliente}.pdf`,
      );

      enqueueSnackbar("Documento tributario creado correctamente", {
        variant: "success",
      });

      setSubmitting(false);
      setOpen(false);
      recargarFacturas();
    } catch (error) {
      setErrorEmision(
        error?.data?.message || "Error al generar el documento tributario",
      );
      setSubmitting(false);
    }
  };

  const cargaInicial = cargandoTarea || !valoresIniciales;
  const monedaNoDisponible =
    valoresIniciales?._error === "MONEDA_NO_DISPONIBLE";

  const renderHeader = (
    dirty,
    previewDisponible = false,
    isSubmitting = false,
  ) => (
    <DialogTitle sx={{ pb: 1, pr: { xs: 11, sm: 11 } }}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.5}
        flexWrap="wrap"
        rowGap={1}
      >
        <Typography variant="h6" component="span">
          Generar Documento Tributario
        </Typography>
        <Chip
          label="Factura No Afecta o Exenta Electrónica · Tipo 34"
          size="small"
          sx={{
            bgcolor: "rgba(255, 149, 0, 0.12)",
            color: "#b26a00",
            fontWeight: 500,
          }}
        />
      </Stack>
      <Stack
        direction="row"
        alignItems="center"
        sx={{ position: "absolute", right: 8, top: 12 }}
      >
        <Tooltip
          title={
            previewDisponible ? "Ver borrador" : "Cargando datos de la tarea..."
          }
        >
          <span>
            <IconButton
              size="small"
              onClick={() => setOpenPreview(true)}
              disabled={!previewDisponible || isSubmitting}
              aria-label="ver borrador"
            >
              <VisibilityOutlinedIcon />
            </IconButton>
          </span>
        </Tooltip>
        <IconButton
          size="small"
          onClick={() => solicitarCierre(dirty)}
          disabled={isSubmitting}
          aria-label="cerrar"
        >
          <CloseIcon />
        </IconButton>
      </Stack>
    </DialogTitle>
  );

  if (errorTarea) {
    return (
      <Dialog id="dialogFactura" open={open} fullWidth maxWidth="sm">
        {renderHeader(false)}
        <DialogContent dividers>
          <Alert severity="error">
            No se pudieron cargar los datos de la tarea. Intenta cerrar y abrir
            el modal nuevamente.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <LoadingButton onClick={() => setOpen(false)}>Cerrar</LoadingButton>
        </DialogActions>
      </Dialog>
    );
  }

  if (monedaNoDisponible) {
    return (
      <Dialog id="dialogFactura" open={open} fullWidth maxWidth="sm">
        {renderHeader(false)}
        <DialogContent dividers>
          <Alert severity="error">
            No hay valor disponible para la moneda{" "}
            <strong>{valoresIniciales.tipoMonedaTarea}</strong> en la tabla de
            indicadores. No es posible emitir el documento hasta que se
            actualice el valor.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <LoadingButton onClick={() => setOpen(false)}>Cerrar</LoadingButton>
        </DialogActions>
      </Dialog>
    );
  }

  if (cargaInicial) {
    return (
      <Dialog
        id="dialogFactura"
        open={open}
        fullWidth
        maxWidth="lg"
        fullScreen={isMobile}
      >
        {renderHeader(false)}
        <DialogContent dividers>
          <Skeleton variant="rectangular" width="100%" height={500} />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <LoadingButton onClick={() => setOpen(false)}>Cerrar</LoadingButton>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog
      id="dialogFactura"
      open={open}
      fullWidth
      maxWidth="lg"
      fullScreen={isMobile}
    >
      <Formik
        initialValues={valoresIniciales}
        validationSchema={facturarTareaSchema}
        validateOnChange
        validateOnBlur
        enableReinitialize={false}
        onSubmit={handleSubmit}
      >
        {(formik) => (
          <Form id="facturarTareaForm" noValidate>
            {renderHeader(formik.dirty, true, formik.isSubmitting)}
            <DialogContent dividers>
              <Grid container spacing={2} sx={{ mt: 0.5 }}>
                <Grid item xs={12} md={7}>
                  <Stack spacing={2}>
                    <ReceptorSection tarea={tareaCompleta} formik={formik} />
                    <DetalleSection tarea={tareaCompleta} formik={formik} />
                  </Stack>
                </Grid>
                <Grid item xs={12} md={5}>
                  <Box
                    sx={{
                      position: isDesktop ? "sticky" : "static",
                      top: isDesktop ? 24 : "auto",
                      maxHeight: isDesktop ? "calc(100vh - 200px)" : "auto",
                      overflowY: isDesktop ? "auto" : "visible",
                    }}
                  >
                    <FacturacionSection
                      tarea={tareaCompleta}
                      monedas={monedas}
                      formik={formik}
                    />
                  </Box>
                </Grid>
              </Grid>

              {errorEmision && (
                <Box sx={{ mt: 2 }}>
                  <Alert severity="error" onClose={() => setErrorEmision(null)}>
                    {errorEmision}
                  </Alert>
                </Box>
              )}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
              <LoadingButton
                onClick={() => solicitarCierre(formik.dirty)}
                disabled={formik.isSubmitting}
              >
                Cancelar
              </LoadingButton>
              <LoadingButton
                variant="outlined"
                onClick={() => setOpenPreview(true)}
                disabled={formik.isSubmitting}
              >
                Ver borrador
              </LoadingButton>
              <LoadingButton
                type="submit"
                disabled={!formik.isValid || formik.isSubmitting}
                loading={formik.isSubmitting}
                variant="contained"
              >
                Generar
              </LoadingButton>
            </DialogActions>

            <PreviewFacturaBorrador
              open={openPreview}
              onClose={() => setOpenPreview(false)}
              emisor={datosEmisor}
              receptor={construirReceptorPreview(formik.values)}
              fecha={new Date()}
              formatoPago={Number(formik.values.formatoPago)}
              tipoDocumento={34}
              items={[
                {
                  glosa: formik.values.glosa,
                  descripcion: formik.values.descripcion || "",
                  cantidad: 1,
                  precio: Number(formik.values.montoFacturar) || 0,
                  moneda: "PESOS",
                  total: Number(formik.values.montoFacturar) || 0,
                },
              ]}
            />
          </Form>
        )}
      </Formik>
    </Dialog>
  );
}
